/**
 * @module agent/adapters/services/PayloadBuilder
 *
 * Builds request payloads for AgentCore API calls.
 * Extracted from AgentCoreAdapter to reduce adapter complexity.
 *
 * Responsibilities:
 * - Extract and validate user info from JWT
 * - Build payload object with user context
 * - Encode payload for transmission
 * - Construct command input object
 */

// Conservative payload size limit for streaming (AWS Lambda limits vary)
const MAX_PAYLOAD_SIZE_BYTES = 200 * 1024; // 200KB

/**
 * Request payload builder for AgentCore API.
 */
class PayloadBuilder {
  /**
   * @param {Object} deps - Dependencies
   * @param {Object} deps.authService - AuthService instance for user extraction
   * @throws {TypeError} If required dependencies are missing
   */
  constructor(deps) {
    if (!deps || typeof deps !== 'object') {
      throw new TypeError('PayloadBuilder: dependencies object required');
    }
    if (!deps.authService) {
      throw new TypeError('PayloadBuilder: authService is required');
    }

    this._authService = deps.authService;
  }

  /**
   * Build encoded payload with user info.
   *
   * @param {Object} options - Build options
   * @param {string} options.content - Message content (prompt)
   * @param {string} options.jwtToken - JWT token for user extraction
   * @returns {Object} { payload: Uint8Array, userEmail: string }
   * @throws {Error} If no user identification found
   */
  buildPayload({ content, jwtToken }) {
    // Extract user info from JWT via AuthService
    const { userId, userEmail: extractedEmail } = this._authService.extractUserInfo(jwtToken);

    // Validate user identification exists
    if (!extractedEmail && !userId) {
      throw new Error('No user identification found in AuthStore or JWT token');
    }

    // Validate userId format if present
    if (userId !== null && userId !== undefined) {
      if (typeof userId !== 'string' || userId.trim() === '') {
        throw new Error('Invalid userId format: must be non-empty string');
      }
    }

    // Validate email format if present
    if (extractedEmail !== null && extractedEmail !== undefined) {
      if (typeof extractedEmail !== 'string' || extractedEmail.trim() === '') {
        throw new Error('Invalid email format: must be non-empty string');
      }
    }

    // Use userId as email fallback for backend compatibility
    const userEmail = extractedEmail || userId;

    // Build payload object
    const payloadObject = {
      prompt: content,
      user: {
        cognitoId: userId,
        email: userEmail
      }
    };

    // Encode payload
    const payload = new TextEncoder().encode(JSON.stringify(payloadObject));

    // Validate payload size
    if (payload.byteLength > MAX_PAYLOAD_SIZE_BYTES) {
      throw new Error(
        `Payload size ${payload.byteLength} bytes exceeds limit of ${MAX_PAYLOAD_SIZE_BYTES} bytes. ` +
          `Prompt length: ${content.length} characters.`
      );
    }

    return {
      payload,
      userEmail
    };
  }

  /**
   * Build complete command input for AgentCore API.
   *
   * @param {Object} options - Build options
   * @param {string} options.content - Message content
   * @param {string} options.jwtToken - JWT token
   * @param {string} options.runtimeSessionId - Session ID
   * @param {string} options.agentRuntimeArn - Agent ARN
   * @param {string} [options.qualifier='DEFAULT'] - Agent qualifier
   * @returns {Object} Command input object
   */
  buildCommandInput({
    content,
    jwtToken,
    runtimeSessionId,
    agentRuntimeArn,
    qualifier = 'DEFAULT'
  }) {
    const { payload } = this.buildPayload({ content, jwtToken });

    return {
      runtimeSessionId,
      agentRuntimeArn,
      qualifier,
      payload
    };
  }
}

export default PayloadBuilder;
